import { type z } from 'zod';

import { PaginationQuerySchema } from '../pagination/pagination';

/** The pagination/sort keys every list query already carries. */
const RESERVED_KEYS = Object.keys(PaginationQuerySchema.shape);

/**
 * Builds a list-endpoint query schema: the shared pagination/sort keys plus a
 * per-resource set of **whitelisted** filters, `.strict()` so an unknown
 * parameter is rejected rather than silently ignored (N7).
 *
 * The schema is only half of the filter law — it decides which parameters may
 * be *sent*; the resource's `FilterSpec` decides which column and operator each
 * one reaches. Neither can express `deletedAt`, so soft-delete scoping stays
 * non-bypassable.
 *
 * @example
 * export const MembersListQuerySchema = makeListQuery({
 *   status: MemberStatusSchema.optional(),
 *   search: z.string().min(1).optional(),
 * });
 * export type MembersListQuery = z.infer<typeof MembersListQuerySchema>;
 */
export function makeListQuery<TFilters extends z.ZodRawShape>(filters: TFilters) {
  for (const key of Object.keys(filters)) {
    if (RESERVED_KEYS.includes(key)) {
      throw new Error(
        `makeListQuery: filter "${key}" collides with a reserved pagination key (${RESERVED_KEYS.join(', ')}).`,
      );
    }
  }

  return PaginationQuerySchema.extend(filters).strict();
}
